import _ from 'lodash';
import app from '$$app';
import moment from 'moment';
import constants from '$$helpers/constants';
import timeTrackingModel from '$$timeTrackingModel';
import projectsModel from '$$projectModel';
import Store from '$$globalStore';
import customDayTemplatesModel from '$$customDayTemplatesModel';
import chartsConfigs from './chartsConfig';
import reportHelpers from '../include/helpers';
import globalToast from '$$vueCmp/toast';

class ProjectsTimeReport {
  constructor() {
    this.timeWorker = null;
    this.reportTasks = {};
    this.calculationMode = null;
  }

  initTimeWorker() {
    if (window.Worker && !this.timeWorker) {
      let version = Date.now();

      if (GT.productVersion && GT.productVersion.buildVersion) {
        version = GT.productVersion.buildVersion;
      }

      this.timeWorker = new Worker('/workers/reports_time_worker.js' + `?${version}`);
    }

    this._initEvents();
  }

  _initEvents() {
    const that = this;

    this.timeWorker.onmessage = function (e) {
      that.calculateProjectsTimeLogReport(e.data);
    };

    this.timeWorker.onerror = function (e) {
      globalToast.error(__('time_calculating_error'));
    };
  }

  calculateTime(projectIds, baselineTasks = [], calcilationMode) {
    this.calculationMode = calcilationMode;
    this.reportTasks = {};
    let preparedWorkerTasks = {};

    const currentFilter = Store.getters['filter/currentFilter'];

    if (projectIds?.length) {
      projectIds?.length && projectIds.forEach(id => {
        const allTasks = Store.getters['tasksModel/getTasksForGantt'](id).data;
        const totalEstimate = allTasks.find(task => task.type === constants.TASK_TYPES.project && !task.parent);
        let projectTasks = allTasks.filter(task => task.type === constants.TASK_TYPES.task || task.type === constants.TASK_TYPES.milestone);

        projectTasks = reportHelpers.prepareTaskResourcesForWorker(projectTasks);

        const filteredTasks = this.returnTasksByCurrentFilter(projectTasks, currentFilter);
        const range = {
          start: currentFilter?.properties?.rangeDate?.start || totalEstimate.start_date,
          end: currentFilter?.properties?.rangeDate?.end || totalEstimate.end_date,
        };

        preparedWorkerTasks[id] = { tasks: filteredTasks, range };
      });
    }

    const baselineTotalEstimate = baselineTasks.find(task => task.type === constants.TASK_TYPES.project && !task.parent);
    let baselineTasksAndMilestones = baselineTasks.filter(task => task.type === constants.TASK_TYPES.task || task.type === constants.TASK_TYPES.milestone);

    baselineTasksAndMilestones = reportHelpers.prepareTaskResourcesForWorker(baselineTasksAndMilestones);

    const filteredBaselineTasks = this.returnTasksByCurrentFilter(baselineTasksAndMilestones, currentFilter);
    const baselineRange = {
      start: currentFilter?.properties?.rangeDate?.start || baselineTotalEstimate?.start_date || moment().startOf('day').format('YYYY-MM-DD HH:mm:ss'),
      end: currentFilter?.properties?.rangeDate?.end || baselineTotalEstimate?.end_date || moment().startOf('day').format('YYYY-MM-DD HH:mm:ss'),
    };

    preparedWorkerTasks = {
      ...preparedWorkerTasks,
      baseline: { tasks: filteredBaselineTasks, range: baselineRange },
    };

    Object.entries(preparedWorkerTasks).forEach(([key, data]) => {
      this.reportTasks[key] = data.tasks;
    });
    this.sendDataToWorker(preparedWorkerTasks);
  }

  sendDataToWorker(tasksData) {
    const projectSettingsMap = {};

    projectsModel.serialize().forEach(settings => {
      projectSettingsMap[settings.gantt_id] = {
        ...settings,
        totalEstimate: Store.getters['tasksModel/getTotalEstimateDataForProject'](settings.gantt_id),
      };
    });

    this.timeWorker.postMessage(_.cloneDeep({
      projectConfigs: reportHelpers.prepareProjectConfigs(),
      projectSettings: projectSettingsMap,
      customDaysData: customDayTemplatesModel.serialize(),
      tasksData,
    }));
  }

  returnTasksByCurrentFilter(tasks, currentFilter) {
    const startDate = currentFilter?.properties?.rangeDate?.start ? moment(currentFilter.properties.rangeDate.start) : null;
    const endDate = currentFilter?.properties?.rangeDate?.end ? moment(currentFilter.properties.rangeDate.end) : null;
    const filterRange = startDate && endDate ? { start: startDate, end: endDate } : null;

    return tasks.filter(task => reportHelpers.returnTaskByRange(task, filterRange)).filter(task => {
      if (!currentFilter?.properties?.assignee?.length) {
        return task;
      }

      if (currentFilter.properties.assignee.includes('-1') && !task.resources.length) {
        return task;
      }

      const assigneeTask = task.resources.find(res => currentFilter.properties.assignee.includes(+res.resource_id));

      if (assigneeTask) {
        return task;
      }
    }).filter(task => {
      if (!currentFilter?.properties?.text?.length) {
        return task;
      }

      return task.text.toLowerCase().startsWith(currentFilter.properties.text.toLowerCase());
    });
  }

  calculateProjectsTimeLogReport(data) {
    let sumLogs;
    let sumEstimation;
    let sumBaselineEstimation;
    let variance;

    let resultCount = 0;

    const reportTasks = data || this.reportTasks;
    const currentFilter = Store.getters['filter/currentFilter'];
    const resultConfig = chartsConfigs.projectsReport(this.calculationMode).default;
    const baselineTasks = reportTasks.baseline;
    const projectsTasks = _.omit(reportTasks, ['baseline']);
    const startDate = currentFilter?.properties?.rangeDate?.start ? moment(currentFilter.properties.rangeDate.start) : null;
    const endDate = currentFilter?.properties?.rangeDate?.end ? moment(currentFilter.properties.rangeDate.end) : null;

    if (!Object.values(projectsTasks).flat().length) {
      app.trigger('onAfterProjectsTimeReportCalculation', null);

      return;
    }

    Object.entries(projectsTasks).forEach(([id, tasks]) => {
      sumLogs = 0;
      sumEstimation = 0;
      sumBaselineEstimation = 0;
      variance = 0;

      const projectData = projectsModel.getProjectDataById(id);

      tasks.forEach(task => {
        const logsData = timeTrackingModel.getLogsByParams(task.gantt_id, task.id);

        if (logsData?.logs) {
          const sum = logsData?.logs.filter(log => {
            const logDate = moment(log.date);

            if (startDate && logDate.isBefore(startDate)) {
              return false;
            }

            if (endDate && logDate.isAfter(endDate)) {
              return false;
            }

            return true;
          }).reduce((sum, log) => sum + log.time, 0);

          // const sum = logsData?.logs.reduce((sum, log) => sum + log.time, 0);

          sumLogs += sum;
        }
      });

      sumLogs = +(sumLogs / 60);

      if (this.calculationMode === constants.OVERVIEW_TYPES.ACTUAL_TO_PLANNED) {
        sumEstimation = tasks.reduce((sum, task) => sum + task.estimation, 0);

        if (sumLogs === 0 && sumEstimation === 0) {
          return;
        }

        variance = sumEstimation - sumLogs;
      }

      if (this.calculationMode === constants.OVERVIEW_TYPES.ACTUAL_TO_BASELINE) {
        sumBaselineEstimation = baselineTasks.reduce((sum, task) => sum + task.estimation, 0);

        if (sumLogs === 0 && sumBaselineEstimation === 0) {
          return;
        }

        variance = sumBaselineEstimation - sumLogs;
      }

      if (this.calculationMode === constants.OVERVIEW_TYPES.PLANNED_TO_BASELINE) {
        sumEstimation = tasks.reduce((sum, task) => sum + task.estimation, 0);
        sumBaselineEstimation = baselineTasks.reduce((sum, task) => sum + task.estimation, 0);

        if (sumEstimation === 0 && sumBaselineEstimation === 0) {
          return;
        }

        variance = sumBaselineEstimation - sumEstimation;
      }

      resultConfig.xAxis.data.push(projectData.name);
      resultConfig.series = resultConfig.series.map(item => {
        const currentCostForGraphicValue1 = this.calculationMode === constants.OVERVIEW_TYPES.PLANNED_TO_BASELINE
          ? sumEstimation
          : sumLogs;
        const nameForTooltipLabel1 = this.calculationMode === constants.OVERVIEW_TYPES.PLANNED_TO_BASELINE
          ? 'estimation'
          : 'logs';

        let currentCostForGraphicValue2 = sumEstimation;
        let nameForTooltipLabel2 = 'estimation';

        if (this.calculationMode === constants.OVERVIEW_TYPES.ACTUAL_TO_BASELINE || this.calculationMode === constants.OVERVIEW_TYPES.PLANNED_TO_BASELINE) {
          currentCostForGraphicValue2 = sumBaselineEstimation;
          nameForTooltipLabel2 = 'baselineEstimation';
        }

        if (item.name === 'time_graphic_data_1') {
          let labelPosition = 'insideTop';

          if (currentCostForGraphicValue1 < 0) {
            labelPosition = 'insideBottom';
          }

          if (currentCostForGraphicValue1 === 0) {
            labelPosition = 'top';
          }

          return {
            ...item,
            data: [
              ...item.data,
              {
                value: currentCostForGraphicValue1,
                label: {
                  name: nameForTooltipLabel1,
                  formatter: `${chartsConfigs.helpers.getTextValue(currentCostForGraphicValue1)}`,
                  position: labelPosition,
                  color: currentCostForGraphicValue1 === 0 ? '#191919' : '#fff',
                },
              },
            ],
          };
        }

        if (item.name === 'time_graphic_data_2') {
          let labelPosition = 'insideTop';

          if (currentCostForGraphicValue2 < 0) {
            labelPosition = 'insideBottom';
          }

          if (currentCostForGraphicValue2 === 0) {
            labelPosition = 'top';
          }

          return {
            ...item,
            data: [
              ...item.data,
              {
                value: currentCostForGraphicValue2,
                label: {
                  name: nameForTooltipLabel2,
                  formatter: `${chartsConfigs.helpers.getTextValue(currentCostForGraphicValue2)}`,
                  position: labelPosition,
                  color: currentCostForGraphicValue2 === 0 ? '#191919' : '#fff',
                },
              },
            ],
          };
        }

        if (item.name === 'variance') {
          let labelPosition = 'insideTop';

          if (variance < 0) {
            labelPosition = 'insideBottom';
          }

          if (variance === 0) {
            labelPosition = 'top';
          }

          return {
            ...item,
            data: [
              ...item.data,
              {
                value: variance,
                label: {
                  name: 'variance',
                  formatter: `${chartsConfigs.helpers.getTextValue(variance)}`,
                  position: labelPosition,
                  color: variance === 0 ? '#191919' : '#fff',
                },
              },
            ],
          };
        }
      });

      resultCount++;
    });

    if (resultCount > 0) {
      app.trigger('onAfterProjectsTimeReportCalculation', resultConfig);

      return;
    }

    app.trigger('onAfterProjectsTimeReportCalculation', null);
  }
}

const projectsTimeReport = new ProjectsTimeReport();

export default projectsTimeReport;
